'use es6';

import PropTypes from 'prop-types';
import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import I18n from 'I18n';
import styled from 'styled-components';
import { BATTLESHIP, CALYPSO_LIGHT } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIButtonGroup from 'UIComponents/button/UIButtonGroup';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIAlert from 'UIComponents/alert/UIAlert';
import H4 from 'UIComponents/elements/headings/H4';
import H6 from 'UIComponents/elements/headings/H6';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UITag from 'UIComponents/tag/UITag';
import EPPreviewIframe from 'editor-components/preview/EPPreviewIframe';
import EPFullScreenModal from 'editor-components/preview/EPFullScreenModal';
import withUsageTracking from 'editor-components/hoc/withUsageTracking';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import Small from 'UIComponents/elements/Small';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CURRENT_REVISION_ID = -1;
const BUFFER_REVISION_ID = 'buffer-revision';
const SIDEBAR_WIDTH = '380px';
const RevisionsSidebar = styled.div.withConfig({
  displayName: "EPRevisionsModal__RevisionsSidebar",
  componentId: "sc-9oyows-0"
})(["display:flex;flex-direction:column;height:100%;width:", ";border-right:1px solid ", ";"], SIDEBAR_WIDTH, BATTLESHIP);
const RevisionsList = styled.div.withConfig({
  displayName: "EPRevisionsModal__RevisionsList",
  componentId: "sc-9oyows-1"
})(["height:calc(100% - 84px);overflow:auto;"]);
const RevisionListItem = styled(UIButton).withConfig({
  displayName: "EPRevisionsModal__RevisionListItem",
  componentId: "sc-9oyows-2"
})(["&[aria-checked='true'],&:hover{background:", ";}"], CALYPSO_LIGHT);
class EPRevisionsModal extends Component {
  constructor(props) {
    super(props);
    this.isBufferSelected = () => {
      const {
        selectedRevision
      } = this.state;
      return selectedRevision.id === BUFFER_REVISION_ID;
    };
    this.updateSelectedRevision = revision => {
      const {
        selectedRevision
      } = this.state;
      if (revision.id !== selectedRevision.id) {
        this.setState({
          selectedRevision: revision
        });
      }
    };
    this.loadMoreRevisions = () => {
      const {
        hasMoreRevisions,
        onLoadMoreRevisions
      } = this.props;
      this.setState({
        isLoadingMoreRevisions: true
      });
      if (hasMoreRevisions) {
        onLoadMoreRevisions();
      }
    };
    this.buildPreviewURL = () => {
      const {
        getPreviewUrl
      } = this.props;
      const {
        selectedRevision
      } = this.state;
      return getPreviewUrl(selectedRevision, selectedRevision.id === BUFFER_REVISION_ID);
    };
    this.chooseRevision = () => {
      const {
        selectedRevision
      } = this.state;
      const {
        onChooseRevision,
        onTrackUsage
      } = this.props;
      this.setState({
        revertingContent: true,
        hideHeaderErrorMessage: false
      });
      onTrackUsage('choose revision click');
      onChooseRevision(selectedRevision);
    };
    this.handleHeaderErrorClose = () => {
      this.setState({
        hideHeaderErrorMessage: true
      });
    };
    this.isPublishedVersion = revision => {
      const {
        revisions,
        isPublished
      } = this.props;
      if (!isPublished || revision.isScheduledUpdate) {
        return false;
      }
      const publishedRevisions = revisions.filter(r => !r.isScheduledUpdate && r.object && r.object.isPublished).sort((r1, r2) => r2.updatedAt - r1.updateAt);
      return publishedRevisions.length > 0 && publishedRevisions[0].id === revision.id;
    };
    this.isScheduledVersion = revision => {
      return revision.object.currentState === 'SCHEDULED' || revision.isScheduledUpdate;
    };
    this.isCurrentDraft = revision => {
      const {
        hasBufferedChanges
      } = this.props;
      if (this.isPublishedVersion(revision)) {
        return false;
      }
      if (hasBufferedChanges) {
        return revision.id === BUFFER_REVISION_ID;
      }
      return revision.id === CURRENT_REVISION_ID;
    };
    this.renderRevision = (revision, index) => {
      const {
        selectedRevision
      } = this.state;
      const {
        totalRevisionCount,
        shouldShowCurrentDraftLabel
      } = this.props;
      const revisionTimestamp = revision.updatedAt;
      const timestamp = revisionTimestamp && !isNaN(revisionTimestamp) ? I18n.moment.portalTz(revisionTimestamp).format('l LT') : '';
      const isActive = revision.id === selectedRevision.id;
      return /*#__PURE__*/_jsx(RevisionListItem, {
        active: isActive,
        "aria-checked": isActive,
        className: 'p-x-7 p-y-4 height-100 width-100',
        "data-test-id": "revision-list-item",
        onClick: () => this.updateSelectedRevision(revision),
        role: "radio",
        use: "unstyled",
        children: /*#__PURE__*/_jsxs("div", {
          className: "display-flex",
          children: [revision.id !== BUFFER_REVISION_ID && /*#__PURE__*/_jsx(H6, {
            className: "p-right-1 m-bottom-0",
            children: `${totalRevisionCount - index}.`
          }), /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(H6, {
              className: 'm-bottom-2',
              children: timestamp
            }), /*#__PURE__*/_jsx("div", {
              "data-test-id": "revision-list-item-author",
              children: revision.user && (revision.user.fullName || revision.user.email) || revision.object && revision.object.authorName || ''
            }), this.isPublishedVersion(revision) && /*#__PURE__*/_jsx(UITag, {
              className: "m-top-1",
              use: "published",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: 'epRevisionsModal.currentVersionLabel'
              })
            }), this.isScheduledVersion(revision) && /*#__PURE__*/_jsx(UITag, {
              className: "m-top-1",
              use: "scheduled",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: 'epRevisionsModal.scheduledVersionLabel'
              })
            }), this.isCurrentDraft(revision) && shouldShowCurrentDraftLabel && /*#__PURE__*/_jsx(UITag, {
              className: "m-top-1",
              use: "thunderdome",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "epRevisionsModal.currentDraft"
              })
            }), revision.warningMessageString && /*#__PURE__*/_jsx(Small, {
              "data-test": "version-warning",
              children: revision.warningMessageString
            })]
          })]
        })
      }, index);
    };
    const selectedRevisionToStart = !props.isPublished && props.hasBufferedChanges ? BUFFER_REVISION_ID : CURRENT_REVISION_ID;
    this.state = {
      selectedRevision: {
        id: selectedRevisionToStart
      },
      revertingContent: false,
      hideHeaderErrorMessage: true,
      isLoadingMoreRevisions: false
    };
  }
  componentDidUpdate(prevProps) {
    const {
      headerErrorMessage,
      isLoadingInitialRevisions,
      revisions
    } = this.props;
    if (!prevProps.headerErrorMessage && headerErrorMessage) {
      this.setState({
        revertingContent: false
      });
    }
    if (prevProps.revisions && prevProps.revisions.length !== this.props.revisions.length) {
      this.setState({
        isLoadingMoreRevisions: false
      });
    }
    if (prevProps.isLoadingInitialRevisions !== isLoadingInitialRevisions) {
      const scheduledRevision = revisions.find(rev => rev.isScheduledUpdate);
      if (scheduledRevision) {
        this.setState({
          selectedRevision: scheduledRevision
        });
      }
    }
  }
  renderHeaderError() {
    const {
      hideHeaderErrorMessage
    } = this.state;
    const {
      headerErrorMessage
    } = this.props;
    if (!hideHeaderErrorMessage && headerErrorMessage) {
      return /*#__PURE__*/_jsx("div", {
        style: {
          maxHeight: '38px'
        },
        children: /*#__PURE__*/_jsx(UIAlert, {
          "data-test-id": "revisions-modal-header-error",
          closeable: true,
          onClose: this.handleHeaderErrorClose,
          style: {
            maxWidth: '100%'
          },
          type: "danger",
          use: "inline",
          children: headerErrorMessage
        })
      });
    }
    return null;
  }
  renderRevisionPreview() {
    const {
      selectedRevision
    } = this.state;
    const {
      getFormDataForPreview,
      getFormUrlForPreview,
      renderPreview
    } = this.props;
    let preview;
    if (typeof renderPreview === 'function') {
      preview = renderPreview({
        selectedRevision
      });
    } else {
      preview = /*#__PURE__*/_jsx(EPPreviewIframe, {
        previewUrl: this.buildPreviewURL(),
        formUrl: getFormUrlForPreview(selectedRevision),
        formData: getFormDataForPreview(selectedRevision)
      });
    }
    return /*#__PURE__*/_jsx("div", {
      className: "flex-grow-1",
      children: preview
    });
  }
  renderBufferRevision() {
    const {
      lastBufferAuthor,
      lastSave
    } = this.props;
    const revision = {
      id: BUFFER_REVISION_ID,
      object: {
        authorName: lastBufferAuthor
      },
      updatedAt: lastSave
    };
    return this.renderRevision(revision, BUFFER_REVISION_ID);
  }
  renderRevisionsSidebar() {
    const {
      hasMoreRevisions,
      revisions,
      paginationLimit,
      isLoadingInitialRevisions,
      hasBufferedChanges,
      subtitle
    } = this.props;
    const {
      isLoadingMoreRevisions
    } = this.state;
    const content = isLoadingInitialRevisions ? /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true
    }) : /*#__PURE__*/_jsxs(RevisionsList, {
      children: [!revisions.length && /*#__PURE__*/_jsx(UIAlert, {
        className: "m-x-7",
        type: "warning",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "epRevisionsModal.noRevisionsText"
        })
      }), /*#__PURE__*/_jsxs(UIButtonGroup, {
        className: "display-flex",
        role: "radiogroup",
        orientation: "vertical",
        children: [hasBufferedChanges && this.renderBufferRevision(), revisions.map(this.renderRevision)]
      }), isLoadingMoreRevisions && /*#__PURE__*/_jsx(UILoadingSpinner, {
        size: 'extra-small',
        className: 'm-left-8'
      }), hasMoreRevisions && !isLoadingMoreRevisions && /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "load-more-revisions",
        onClick: this.loadMoreRevisions,
        className: 'p-left-7' + (subtitle ? " p-bottom-3" : ""),
        size: "md",
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "epRevisionsModal.loadMoreButtonText",
          options: {
            count: paginationLimit
          }
        })
      })]
    });
    const titleClasses = 'p-top-4 p-left-7' + (subtitle ? " m-bottom-2" : "");
    return /*#__PURE__*/_jsxs(RevisionsSidebar, {
      children: [/*#__PURE__*/_jsx(H4, {
        className: titleClasses,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'epRevisionsModal.headerText'
        })
      }), subtitle && /*#__PURE__*/_jsx("p", {
        "data-test-id": "revisions-modal-subtitle",
        className: "p-x-7",
        children: /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: subtitle
        })
      }), content]
    });
  }
  renderHeaderButton() {
    const {
      revertingContent
    } = this.state;
    const {
      disabled,
      revisions,
      shouldRestoreToBuffer,
      preventRevertLoading
    } = this.props;
    const _disabled = this.isBufferSelected() || disabled;
    const button = /*#__PURE__*/_jsx(UITooltip, {
      disabled: !this.isBufferSelected(),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'components.modals.revisionsViewModal.cantRestoreBufferTooltip'
      }),
      placement: "top right",
      children: /*#__PURE__*/_jsx(UILoadingButton, {
        loading: !preventRevertLoading && revertingContent,
        use: "primary",
        onClick: this.chooseRevision,
        disabled: _disabled || !revisions.length,
        "data-test-id": "select-revision-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `epRevisionsModal.restoreRevision`
        })
      })
    });
    if (revisions.length && !revertingContent && shouldRestoreToBuffer) {
      return /*#__PURE__*/_jsx(UITooltip, {
        placement: "bottom left",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'epRevisionsModal.publishWarning'
        }),
        children: button
      });
    }
    return button;
  }
  render() {
    const {
      onClose
    } = this.props;
    return /*#__PURE__*/_jsx(EPFullScreenModal, {
      onClose: onClose,
      headerContent: this.renderHeaderError(),
      headerButton: this.renderHeaderButton(),
      children: /*#__PURE__*/_jsxs("div", {
        className: "display-flex",
        style: {
          height: '100%'
        },
        children: [this.renderRevisionsSidebar(), this.renderRevisionPreview()]
      })
    });
  }
}
EPRevisionsModal.propTypes = {
  disabled: PropTypes.bool.isRequired,
  getPreviewUrl: PropTypes.func,
  getFormDataForPreview: PropTypes.func.isRequired,
  getFormUrlForPreview: PropTypes.func.isRequired,
  hasMoreRevisions: PropTypes.bool.isRequired,
  headerErrorMessage: PropTypes.string,
  onChooseRevision: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onLoadMoreRevisions: PropTypes.func.isRequired,
  onTrackUsage: PropTypes.func.isRequired,
  paginationLimit: PropTypes.number.isRequired,
  revisions: PropTypes.array.isRequired,
  shouldRestoreToBuffer: PropTypes.bool.isRequired,
  totalRevisionCount: PropTypes.number.isRequired,
  isLoadingInitialRevisions: PropTypes.bool.isRequired,
  preventRevertLoading: PropTypes.bool,
  renderPreview: PropTypes.func,
  subtitle: PropTypes.node,
  isPublished: PropTypes.bool,
  hasBufferedChanges: PropTypes.bool,
  lastBufferAuthor: PropTypes.string,
  lastSave: PropTypes.instanceOf(Date),
  shouldShowCurrentDraftLabel: PropTypes.bool
};
EPRevisionsModal.defaultProps = {
  disabled: false,
  getFormDataForPreview: emptyFunction,
  getFormUrlForPreview: emptyFunction,
  getPreviewUrl: emptyFunction,
  revisions: [],
  shouldRestoreToBuffer: true,
  preventRevertLoading: false,
  isPublished: true,
  hasBufferedChanges: false,
  shouldShowCurrentDraftLabel: true
};
export default withUsageTracking(EPRevisionsModal);